import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  CleaningQueueItem,
  CleaningQueueListPayload,
  CleaningQueueSummary,
} from '../types/cleaningQueue';

/**
 * Backend contract — implementar en el API:
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ### GET /api/admin/cleaning-queue
 * Auth:   Bearer token (solo admin)
 * ─────────────────────────────────────────────────────────────────────────────
 * Query params (todos opcionales):
 *   - search    (string)  búsqueda parcial en título, SKU y nombre de consignador
 *   - page      (number)  default 1
 *   - pageSize  (number)  default 20
 *
 * Los productos nuevos siempre se reciben limpios, por lo que este endpoint
 * solo devuelve productos usados donde `is_clean = false`, ordenados por
 * `received_at` ASC (más antiguo primero). No es necesario un parámetro de filtro.
 *
 * Respuesta JSON:
 * ```json
 * {
 *   "message": "OK",
 *   "data": {
 *     "items": [
 *       {
 *         "id": 1,
 *         "artId": "UU-00042",
 *         "title": "Nike Dunk Low 'Panda'",
 *         "consignorName": "Carlos Rodriguez",
 *         "sizeCm": "28.5",
 *         "isNew": false,
 *         "receivedAt": "2024-01-15T10:30:00Z"
 *       }
 *     ],
 *     "total": 12,
 *     "page": 1,
 *     "pageSize": 20,
 *     "summary": {
 *       "totalPending": 12
 *     }
 *   }
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ### PATCH /api/admin/inventory/:inventoryId/mark-clean
 * Auth:   Bearer token (solo admin)
 * Body:   vacío o `{}` (no requiere body)
 * ─────────────────────────────────────────────────────────────────────────────
 * - Establece `is_clean = true` y opcionalmente `cleaned_at = NOW()`.
 * - El ítem debe dejar de aparecer en la cola (ya no cumple el filtro is_clean = false).
 *
 * Respuesta 200:
 * ```json
 * {
 *   "message": "Ítem marcado como limpio",
 *   "data": { "inventoryId": 1 }
 * }
 * ```
 *
 * Errores posibles:
 *   - 404 → ítem no encontrado
 *   - 409 → el ítem ya estaba marcado como limpio (opcional, puede devolver 200)
 *   - 403 → no admin
 */

// ─── Parsers ──────────────────────────────────────────────────────────────────

function parseCleaningQueueItem(raw: unknown): CleaningQueueItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;

  const isNew = (() => {
    const v = r.isNew ?? r.is_new ?? r.isNuevo;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    return false;
  })();

  const receivedAt = (() => {
    const v = r.receivedAt ?? r.received_at ?? r.createdAt ?? r.created_at;
    return typeof v === 'string' && v.trim() ? v.trim() : new Date(0).toISOString();
  })();

  return {
    id,
    artId: (() => {
      const v = r.artId ?? r.art_id;
      if (v == null || v === '') return null;
      const s = String(v).trim();
      return s || null;
    })(),
    title: String(r.title ?? '').trim(),
    consignorName: String(r.consignorName ?? r.consignor_name ?? '').trim(),
    sizeCm: (() => {
      const v = r.sizeCm ?? r.size_cm ?? r.size;
      return v != null && String(v).trim() ? String(v).trim() : null;
    })(),
    isNew,
    receivedAt,
  };
}

function parseSummary(raw: unknown): CleaningQueueSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const totalPending = Number(r.totalPending ?? r.total_pending);
  if (!Number.isFinite(totalPending)) return null;
  return { totalPending };
}

function parseListPayload(
  body: unknown,
  fallbackPage: number,
  fallbackPageSize: number
): CleaningQueueListPayload {
  const empty: CleaningQueueListPayload = {
    items: [],
    total: 0,
    page: fallbackPage,
    pageSize: fallbackPageSize,
    summary: null,
  };
  if (!body || typeof body !== 'object') return empty;
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const rawItems = inner.items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(parseCleaningQueueItem).filter((x): x is CleaningQueueItem => x !== null)
    : [];
  const total =
    typeof inner.total === 'number' && Number.isFinite(inner.total) ? inner.total : items.length;
  const page = typeof inner.page === 'number' && inner.page >= 1 ? inner.page : fallbackPage;
  const pageSize =
    typeof inner.pageSize === 'number' && inner.pageSize >= 1 ? inner.pageSize : fallbackPageSize;
  return { items, total, page, pageSize, summary: parseSummary(inner.summary) };
}

// ─── Exported functions ───────────────────────────────────────────────────────

export interface CleaningQueueListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
}

export type CleaningQueueListResult =
  | { ok: true; data: CleaningQueueListPayload }
  | { ok: false; error: string };

export async function fetchCleaningQueue(
  query: CleaningQueueListQuery = {}
): Promise<CleaningQueueListResult> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 20;
  try {
    const params: Record<string, string | number> = { page, pageSize };
    if (query.search?.trim()) params.search = query.search.trim();
    const { data } = await authClient.get<unknown>('/api/admin/cleaning-queue', { params });
    return { ok: true, data: parseListPayload(data, page, pageSize) };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'El endpoint de cola de limpieza aún no está disponible.'
        : null) ??
      'No se pudo cargar la cola de limpieza.';
    return { ok: false, error: msg };
  }
}

export type MarkCleanResult = { ok: true; inventoryId: number } | { ok: false; error: string };

export async function markItemClean(inventoryId: number): Promise<MarkCleanResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const { data } = await authClient.patch<unknown>(
      `/api/admin/inventory/${inventoryId}/mark-clean`,
      {}
    );
    const o = data as Record<string, unknown>;
    const inner = (o?.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
      string,
      unknown
    >;
    const iid = Number(inner?.inventoryId ?? inner?.id ?? inventoryId);
    return { ok: true, inventoryId: iid };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'No se encontró el ítem de inventario.' : null) ??
      'No se pudo marcar el ítem como limpio.';
    return { ok: false, error: msg };
  }
}
